import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LifeBuoy, Filter, Download, RefreshCw, Users2, ClipboardCheck, Loader2, Timer } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { endpoints, getTenantScope, API } from "@/lib/api";
import { toast } from "sonner";
import { NoStaffNote } from "@/components/common/NoStaffNote";

const STATUS_TONE = {
  "New": "bg-slate-100 text-slate-700 border-slate-200",
  "Assigned": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "In Progress": "bg-amber-50 text-amber-800 border-amber-200",
  "Waiting for Client": "bg-purple-50 text-purple-700 border-purple-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const PRIORITY_TONE = {
  "Low": "bg-slate-100 text-slate-600 border-slate-200",
  "Normal": "bg-blue-50 text-blue-700 border-blue-200",
  "High": "bg-amber-100 text-amber-800 border-amber-300",
  "Urgent": "bg-rose-100 text-rose-800 border-rose-300",
};

const fmt = (s) => s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function AssistanceRequestsPage() {
  const [searchParams] = useSearchParams();
  const clientQP = searchParams.get("client");
  const [scope, setScope] = useState(getTenantScope());
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], categories: [], priorities: [] });
  const [clientFilter, setClientFilter] = useState(clientQP || "all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = {};
    if (clientFilter !== "all") params.client_slug = clientFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    endpoints.listAssistanceRequests(params).then(setRows).catch(() => setRows([]));
  };

  useEffect(() => {
    endpoints.assistanceMetadata().then(setMeta).catch(() => {});
    endpoints.listClients().then(setClients).catch(() => {});
    endpoints.listConsultants ? endpoints.listConsultants().then(setConsultants).catch(() => {}) : null;
    fetch(`${API}/consultants`).then(r => r.json()).then(setConsultants).catch(() => {});
    const onScope = () => setScope(getTenantScope());
    window.addEventListener("isoquest:scope-changed", onScope);
    return () => window.removeEventListener("isoquest:scope-changed", onScope);
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientFilter, statusFilter]);

  const isClient = scope?.role === "client";
  const filtered = useMemo(() => rows.filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.request_number || "").toLowerCase().includes(s)
      || (r.subject || "").toLowerCase().includes(s)
      || (r.category || "").toLowerCase().includes(s)
      || (r.client_name || "").toLowerCase().includes(s)
      || (r.assigned_to || "").toLowerCase().includes(s);
  }), [rows, q]);

  const stats = useMemo(() => ({
    total: rows.length,
    new: rows.filter(r => r.status === "New").length,
    inProgress: rows.filter(r => r.status === "In Progress").length,
    waiting: rows.filter(r => r.status === "Waiting for Client").length,
    completed: rows.filter(r => r.status === "Completed").length,
  }), [rows]);

  const openDetail = (r) => setDetail(r);
  const closeDetail = () => setDetail(null);

  const patchRow = async (patch, actor) => {
    if (!detail) return;
    try {
      const updated = await endpoints.updateAssistanceRequest(detail.id, { ...patch, actor });
      setDetail(updated);
      setRows((rs) => rs.map(r => r.id === updated.id ? updated : r));
      toast.success("Updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const accept = async (actor) => {
    if (!detail) return;
    try {
      const updated = await endpoints.acceptAssistanceRequest(detail.id, { actor });
      setDetail(updated);
      setRows((rs) => rs.map(r => r.id === updated.id ? updated : r));
      toast.success("Accepted");
    } catch (e) { toast.error(e.response?.data?.detail || "Accept failed"); }
  };
  const addNote = async (note, author) => {
    if (!detail) return;
    try {
      const updated = await endpoints.addAssistanceNote(detail.id, { note, author });
      setDetail(updated);
      setRows((rs) => rs.map(r => r.id === updated.id ? updated : r));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md">
            <LifeBuoy className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Client Assistance Requests</h1>
            <p className="text-xs text-slate-500">Track, assign and progress requests raised by clients through the portal.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="assistance-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <a href={endpoints.exportAssistanceCsvUrl(clientFilter !== "all" ? { client_slug: clientFilter } : {})}
             className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="assistance-export">
            <Download className="h-4 w-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Stat label="Total" value={stats.total} tone="indigo" />
        <Stat label="New" value={stats.new} tone="slate" />
        <Stat label="In progress" value={stats.inProgress} tone="amber" icon={Loader2} />
        <Stat label="Waiting client" value={stats.waiting} tone="purple" icon={Timer} />
        <Stat label="Completed" value={stats.completed} tone="emerald" icon={ClipboardCheck} />
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Input data-testid="assistance-search" placeholder="Search request #, subject, client..." value={q} onChange={e => setQ(e.target.value)} />
        {!isClient && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="assistance-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="assistance-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(meta.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 dark:bg-slate-900/40 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2.5 px-3">Request #</th>
                  {!isClient && <th className="py-2.5 px-3">Client</th>}
                  <th className="py-2.5 px-3">Category</th>
                  <th className="py-2.5 px-3">Subject</th>
                  <th className="py-2.5 px-3">Priority</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Assigned to</th>
                  <th className="py-2.5 px-3">Submitted</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={isClient ? 8 : 9} className="text-center py-10 text-slate-500">No assistance requests match your filters.</td></tr>
                )}
                {filtered.map(r => (
                  <tr key={r.id} data-testid={`assistance-row-${r.id}`} className="border-b hover:bg-slate-50/60 dark:hover:bg-white/5 cursor-pointer" onClick={() => openDetail(r)}>
                    <td className="py-2.5 px-3 font-mono text-xs">{r.request_number}</td>
                    {!isClient && <td className="py-2.5 px-3">{r.client_name}</td>}
                    <td className="py-2.5 px-3"><Badge variant="secondary" className="text-[11px]">{r.category}</Badge></td>
                    <td className="py-2.5 px-3 max-w-[280px] truncate">{r.subject}</td>
                    <td className="py-2.5 px-3"><Badge className={`${PRIORITY_TONE[r.priority] || ""}`}>{r.priority}</Badge></td>
                    <td className="py-2.5 px-3"><Badge className={`${STATUS_TONE[r.status] || ""}`}>{r.status}</Badge></td>
                    <td className="py-2.5 px-3">{r.assigned_to || <span className="text-slate-400">Unassigned</span>}</td>
                    <td className="py-2.5 px-3 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                    <td className="py-2.5 px-3 flex items-center gap-1">
                      <Button size="sm" variant="ghost" data-testid={`assistance-open-${r.id}`}>Open</Button>
                      {!isClient && (
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`assistance-delete-${r.id}`}
                          className="text-slate-400 hover:text-rose-600"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Delete ${r.request_number}?`)) return;
                            try { await endpoints.deleteAssistanceRequest(r.id); toast.success("Request deleted"); load(); }
                            catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AssistanceDetailModal
        req={detail}
        open={!!detail}
        onOpenChange={(o) => { if (!o) closeDetail(); }}
        meta={meta}
        consultants={consultants}
        isClient={isClient}
        onPatch={patchRow}
        onAccept={accept}
        onNote={addNote}
      />
    </div>
  );
}

function Stat({ label, value, tone = "indigo", icon: Icon }) {
  const toneCls = {
    indigo: "text-indigo-600",
    slate: "text-slate-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
    emerald: "text-emerald-600",
  }[tone];
  return (
    <Card className="glass-card">
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
          {Icon && <Icon className="h-3 w-3" />} {label}
        </div>
        <div className={`text-2xl font-extrabold mt-1 ${toneCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function AssistanceDetailModal({ req, open, onOpenChange, meta, consultants, isClient, onPatch, onAccept, onNote }) {
  const [note, setNote] = useState("");
  const [actor, setActor] = useState("");
  useEffect(() => { setNote(""); if (consultants?.length && !actor) setActor(consultants[0].name); }, [req?.id, consultants]);
  if (!req) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="assistance-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant="secondary" className="font-mono">{req.request_number}</Badge>
            <span className="truncate">{req.subject}</span>
          </DialogTitle>
          <DialogDescription>
            {req.category} · {req.client_name} · requested by <strong>{req.requested_by}</strong> on {fmt(req.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Field label="Status"><Badge className={STATUS_TONE[req.status]}>{req.status}</Badge></Field>
            <Field label="Priority"><Badge className={PRIORITY_TONE[req.priority]}>{req.priority}</Badge></Field>
            <Field label="Assigned to">{req.assigned_to || <span className="text-slate-400">Unassigned</span>}</Field>
            <Field label="Accepted by">{req.accepted_by || <span className="text-slate-400">—</span>}</Field>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Details</div>
            <div className="rounded-lg border p-3 bg-white/70 dark:bg-slate-900/40 text-sm whitespace-pre-wrap">{req.details}</div>
          </div>

          {!isClient && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Actor</div>
                <Select value={actor} onValueChange={setActor}>
                  <SelectTrigger data-testid="assistance-actor"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {consultants.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <NoStaffNote list={consultants} testid="assistance-no-staff-note" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Change status</div>
                <Select value={req.status} onValueChange={(v) => onPatch({ status: v }, actor)}>
                  <SelectTrigger data-testid="assistance-status-change"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Actions</div>
                <div className="flex gap-2">
                  <Button data-testid="assistance-accept" size="sm" variant="outline" onClick={() => onAccept(actor)} disabled={!!req.accepted_by}>Accept</Button>
                  <Select value={req.assigned_to || ""} onValueChange={(v) => {
                    const c = consultants.find(x => x.name === v);
                    onPatch({ assigned_to: v, assigned_to_email: c?.email || "" }, actor);
                  }}>
                    <SelectTrigger data-testid="assistance-assign" className="w-40"><SelectValue placeholder="Assign to..." /></SelectTrigger>
                    <SelectContent>
                      {consultants.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Progress notes</div>
            <div className="max-h-40 overflow-y-auto space-y-2 rounded-lg border p-2 bg-slate-50/50 dark:bg-slate-900/30">
              {(req.progress_notes || []).length === 0 && <div className="text-xs text-slate-400 text-center py-2">No notes yet.</div>}
              {(req.progress_notes || []).map(n => (
                <div key={n.id} className="text-sm bg-white dark:bg-slate-900/50 border rounded-md p-2">
                  <div className="text-[11px] text-slate-500 flex justify-between mb-1"><span>{n.author}</span><span>{fmt(n.created_at)}</span></div>
                  <div className="whitespace-pre-wrap">{n.note}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Textarea data-testid="assistance-note-input" placeholder="Add a progress note..." rows={2} value={note} onChange={e => setNote(e.target.value)} />
              <Button data-testid="assistance-note-add" onClick={() => { if (note.trim()) { onNote(note.trim(), isClient ? undefined : actor); setNote(""); } }}>Add</Button>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">History</div>
            <ol className="relative border-l-2 border-slate-200 dark:border-white/10 ml-2 pl-3 space-y-2 max-h-48 overflow-y-auto">
              {(req.history || []).slice().reverse().map(h => (
                <li key={h.id} className="text-xs">
                  <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-indigo-500 border-2 border-white dark:border-slate-900"></span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{h.action.replace(/_/g, " ")}</span>
                    <span className="text-slate-500">by {h.actor}</span>
                    <span className="text-slate-400">· {fmt(h.at)}</span>
                  </div>
                  {h.details && <div className="text-slate-600 dark:text-slate-300">{h.details}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm mt-1">{children}</div>
    </div>
  );
}
